'use client';

import { isNil } from 'es-toolkit';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';

import { jsonParse } from '@savvy/utils/error-handling/json-parse';

const STORAGE_KEY = LOCAL_STORAGE_KEYS.DEFAULT_ACCOUNTS_COLUMNS;

export type AccountsTableColumn =
  | 'Account Name'
  | 'Balance'
  | 'Cash & Money Market'
  | 'Household'
  | 'Primary Advisor'
  | 'Strategy'
  | 'Orion Fee Schedule'
  | 'Orion Frequency'
  | 'Bill Style'
  | 'Orion Pay Method'
  | 'Start Date'
  | 'Recurring Adjustment Start Date'
  | 'Recurring Adjustment End Date'
  | 'Recurring Adjustment Schedule'
  | 'Fee Type'
  | 'Amount Type'
  | 'Flat Fee Amount'
  | 'Minimum Fee';

export const DEFAULT_ACCOUNTS_TABLE_COLUMNS: readonly AccountsTableColumn[] = [
  'Account Name',
  'Balance',
  'Household',
  'Strategy',
  'Fee Type',
  'Amount Type',
  'Flat Fee Amount',
  'Minimum Fee',
] as const;

export function getDefaultAccountsColumns(): AccountsTableColumn[] {
  const userDefaultColumns = localStorage.getItem(STORAGE_KEY);
  if (isNil(userDefaultColumns)) {
    return [...DEFAULT_ACCOUNTS_TABLE_COLUMNS];
  }

  const parsed = jsonParse(userDefaultColumns);
  if (parsed.isErr()) {
    return [...DEFAULT_ACCOUNTS_TABLE_COLUMNS];
  }

  return isValidColumnArray(parsed.value) ? parsed.value : [...DEFAULT_ACCOUNTS_TABLE_COLUMNS];
}

export function setDefaultAccountsColumns(columns: AccountsTableColumn[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
}

export const ACCOUNTS_COLUMN_TO_SLUG = {
  'Account Name': 'name',
  Balance: 'balance',
  'Cash & Money Market': 'cash-mm',
  Household: 'household',
  'Primary Advisor': 'primary-advisor',
  Strategy: 'strategy',
  'Orion Fee Schedule': 'orion-fee',
  'Orion Frequency': 'orion-freq',
  'Bill Style': 'bill-style',
  'Orion Pay Method': 'orion-pay',
  'Start Date': 'start-date',
  'Recurring Adjustment Start Date': 'bill-start-date',
  'Recurring Adjustment End Date': 'bill-end-date',
  'Recurring Adjustment Schedule': 'bill-schedule-name',
  'Fee Type': 'fee-type',
  'Amount Type': 'amount-type',
  'Flat Fee Amount': 'flat-fee',
  'Minimum Fee': 'min-fee',
} as const satisfies Record<AccountsTableColumn, string>;

export type AccountsColumnSlug = (typeof ACCOUNTS_COLUMN_TO_SLUG)[AccountsTableColumn];

// Inverse mapping of ACCOUNTS_COLUMN_TO_SLUG
export const ACCOUNTS_SLUG_TO_COLUMN: Record<string, AccountsTableColumn> = {
  name: 'Account Name',
  balance: 'Balance',
  'cash-mm': 'Cash & Money Market',
  household: 'Household',
  'primary-advisor': 'Primary Advisor',
  strategy: 'Strategy',
  'orion-fee': 'Orion Fee Schedule',
  'orion-freq': 'Orion Frequency',
  'bill-style': 'Bill Style',
  'orion-pay': 'Orion Pay Method',
  'start-date': 'Start Date',
  'bill-start-date': 'Recurring Adjustment Start Date',
  'bill-end-date': 'Recurring Adjustment End Date',
  'bill-schedule-name': 'Recurring Adjustment Schedule',
  'fee-type': 'Fee Type',
  'amount-type': 'Amount Type',
  'flat-fee': 'Flat Fee Amount',
  'min-fee': 'Minimum Fee',
} as const;

export function accountsColumnsToSlugs(columns: readonly AccountsTableColumn[]): AccountsColumnSlug[] {
  return columns.map((c) => ACCOUNTS_COLUMN_TO_SLUG[c]);
}

export function accountsColumnsFromSlugs(slugs: readonly string[]): AccountsTableColumn[] {
  return slugs.map((s) => ACCOUNTS_SLUG_TO_COLUMN[s]).filter((v): v is AccountsTableColumn => !isNil(v));
}

function isValidColumnArray(value: unknown): value is AccountsTableColumn[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item): item is AccountsTableColumn =>
        typeof item === 'string' &&
        [
          'Account Name',
          'Balance',
          'Cash & Money Market',
          'Household',
          'Primary Advisor',
          'Strategy',
          'Orion Fee Schedule',
          'Orion Frequency',
          'Bill Style',
          'Orion Pay Method',
          'Start Date',
          'Recurring Adjustment Start Date',
          'Recurring Adjustment End Date',
          'Recurring Adjustment Schedule',
          'Fee Type',
          'Amount Type',
          'Flat Fee Amount',
          'Minimum Fee',
        ].includes(item),
    )
  );
}
